import type {Address, WalletClient} from 'viem';
import {config} from './config.js';
import {walletClientFor} from './chain.js';

/**
 * Wallet connection over EIP-6963, which is how a browser announces the wallets
 * actually installed. No WalletConnect project id and no vendor SDK: the user
 * picks from what their own browser reports, and nothing is phoned home.
 */

export interface Provider {
  info: {uuid: string; name: string; icon: string; rdns: string};
  provider: EIP1193Provider;
}

interface EIP1193Provider {
  request(args: {method: string; params?: unknown[] | object}): Promise<unknown>;
  on?(event: string, handler: (...args: never[]) => void): void;
  removeListener?(event: string, handler: (...args: never[]) => void): void;
}

const providers = new Map<string, Provider>();
const listeners = new Set<() => void>();

export interface WalletState {
  address: Address | null;
  chainId: number | null;
  providerName: string | null;
  client: WalletClient | null;
}

export const wallet: WalletState = {address: null, chainId: null, providerName: null, client: null};

let active: Provider | null = null;

export function onWalletChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function emit() {
  for (const fn of listeners) fn();
}

/** Wallets announce themselves in response to this; the list fills in over a tick or two. */
export function discoverProviders(): Provider[] {
  window.dispatchEvent(new Event('eip6963:requestProvider'));
  return [...providers.values()];
}

window.addEventListener('eip6963:announceProvider', (event: Event) => {
  const detail = (event as CustomEvent<Provider>).detail;
  if (!detail?.info?.uuid) return;
  providers.set(detail.info.uuid, detail);
  emit();
});
window.dispatchEvent(new Event('eip6963:requestProvider'));

// A wallet that predates EIP-6963 still injects window.ethereum.
const legacy = (window as {ethereum?: EIP1193Provider}).ethereum;
if (legacy && providers.size === 0) {
  providers.set('injected', {
    info: {uuid: 'injected', name: 'Browser wallet', icon: '', rdns: 'injected'},
    provider: legacy,
  });
}

export class WalletError extends Error {}

export async function connect(chosen?: Provider): Promise<void> {
  const list = discoverProviders();
  const target = chosen ?? list[0];
  if (!target) {
    throw new WalletError(
      'No wallet found in this browser. Install one that supports Robinhood Chain, then reload.',
    );
  }
  const accounts = (await target.provider.request({method: 'eth_requestAccounts'})) as Address[];
  const address = accounts[0];
  if (!address) throw new WalletError('The wallet returned no account.');

  const chainIdHex = (await target.provider.request({method: 'eth_chainId'})) as string;
  active = target;
  wallet.address = address;
  wallet.chainId = Number(chainIdHex);
  wallet.providerName = target.info.name;
  wallet.client = walletClientFor(target.provider, address);

  target.provider.on?.('accountsChanged', ((next: Address[]) => {
    wallet.address = next[0] ?? null;
    wallet.client = wallet.address && active ? walletClientFor(active.provider, wallet.address) : null;
    emit();
  }) as never);
  target.provider.on?.('chainChanged', ((hex: string) => {
    wallet.chainId = Number(hex);
    emit();
  }) as never);

  emit();
}

export function disconnect(): void {
  active = null;
  wallet.address = null;
  wallet.chainId = null;
  wallet.providerName = null;
  wallet.client = null;
  emit();
}

export function onRightChain(): boolean {
  return wallet.chainId === config.chainId;
}

/**
 * Asks the wallet to switch, and to add the chain if it does not know it. 4902
 * is the "unrecognised chain" code; some wallets nest it one level down.
 */
export async function switchChain(): Promise<void> {
  if (!active) throw new WalletError('Connect a wallet first.');
  const hex = `0x${config.chainId.toString(16)}`;
  try {
    await active.provider.request({method: 'wallet_switchEthereumChain', params: [{chainId: hex}]});
  } catch (e) {
    const code = (e as {code?: number; data?: {originalError?: {code?: number}}})?.code
      ?? (e as {data?: {originalError?: {code?: number}}})?.data?.originalError?.code;
    if (code !== 4902) throw e;
    await active.provider.request({
      method: 'wallet_addEthereumChain',
      params: [{
        chainId: hex,
        chainName: config.chainName,
        nativeCurrency: {name: 'Ether', symbol: 'ETH', decimals: 18},
        rpcUrls: [config.rpcUrl],
        ...(config.explorerUrl ? {blockExplorerUrls: [config.explorerUrl]} : {}),
      }],
    });
  }
}
